import { AppWidgetItem, SelectedAppsWidgets } from './addAppWidgetCatalog'
import {
  LayoutVariant,
  TemplateSelection,
  WorkspaceLayout,
  WorkspacePrivacy
} from './CreateWorkspaceModal'

/** Server-side enum name for each local layout variant. */
export const LAYOUT_VARIANT_TO_SERVER: Record<LayoutVariant, string> = {
  single: 'SinglePrimary',
  columns: 'TwoColumn',
  'left-stack': 'PrimaryWithStack',
  grid: 'Quadrant'
}

/** Local layout variant for each server-side enum name (reverse of the above). */
export const SERVER_TO_LAYOUT_VARIANT: Record<string, LayoutVariant> = {
  SinglePrimary: 'single',
  TwoColumn: 'columns',
  PrimaryWithStack: 'left-stack',
  Quadrant: 'grid'
}

/** Server itemType values for workspace items. */
export const WORKSPACE_ITEM_TYPE = {
  APPLICATION: 'Application'
} as const

/** Server-side workspace scope (privacy) values. */
export const WORKSPACE_SCOPE = {
  PERSONAL: 'Personal',
  TEAM: 'Team'
} as const

/** Team membership roles returned by the teams API. */
export const TEAM_ROLE = {
  OWNER: 'Owner',
  CONTRIBUTOR: 'Contributor',
  MEMBER: 'Member'
} as const

/** Team lifecycle status values. */
export const TEAM_STATUS = {
  ACTIVE: 'Active'
} as const

/** Server-side workspace scope value (union of {@link WORKSPACE_SCOPE}). */
export type WorkspaceScope = (typeof WORKSPACE_SCOPE)[keyof typeof WORKSPACE_SCOPE]

/** Template selection kinds for the create-workspace wizard (discriminated union tag). */
export const TEMPLATE_TYPE = {
  BLANK: 'blank',
  WORKSPACE: 'workspace',
  CENTRALI: 'centrali'
} as const

/** Template source tabs shown on the "Choose a template" step. */
export const TEMPLATE_TAB = {
  MINE: 'mine',
  CENTRALI: 'centrali'
} as const

/** One of the template source tabs (union of {@link TEMPLATE_TAB}). */
export type TemplateTab = (typeof TEMPLATE_TAB)[keyof typeof TEMPLATE_TAB]

export interface CreateWorkspaceItem {
  itemType: typeof WORKSPACE_ITEM_TYPE.APPLICATION
  applicationId: number
  /** Omitted unless a real server widget id is available. */
  widgetId?: number
  position: number
  configuration: string
}

export interface CreateWorkspacePayload {
  name: string
  privacy: WorkspaceScope
  /** Team GUID (or legacy numeric id). Omitted unless a team is explicitly selected. */
  teamId?: string | number
  layout: string
  includeAliPanel: boolean
  /** Omitted unless a numeric template workspace id is available. */
  templateWorkspaceId?: number
  items: CreateWorkspaceItem[]
}

/** Coerce a possibly-string id to a number, returning 0 when not numeric. */
const toNumericId = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Resolve the numeric applicationId for a selected app item. */
const resolveApplicationId = (app: AppWidgetItem): number =>
  toNumericId(app?.applicationId ?? app?.id)

/**
 * Resolve a teamId for the payload. Team ids are GUID strings; those are kept
 * as-is. Legacy numeric (or numeric-string) ids are coerced to a number. Empty,
 * null or zero values are dropped (returns undefined).
 */
const resolveTeamId = (value: unknown): string | number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0 ? value : undefined
  const str = String(value).trim()
  if (!str) return undefined
  const num = Number(str)
  return Number.isFinite(num) && String(num) === str ? num : str
}

/** Resolve a numeric templateWorkspaceId from the selected template (0 when unavailable). */
const resolveTemplateWorkspaceId = (template?: TemplateSelection): number => {
  if (!template) return 0
  if (template.type === TEMPLATE_TYPE.WORKSPACE) return toNumericId(template.workspace?.id)
  if (template.type === TEMPLATE_TYPE.CENTRALI) return toNumericId(template.preset?.id)
  return 0
}

export interface BuildCreateWorkspacePayloadArgs {
  name: string
  privacy: WorkspacePrivacy
  layout?: WorkspaceLayout
  teamId?: string | number | null
  template?: TemplateSelection
  selectedAppsWidgets?: SelectedAppsWidgets
}

/**
 * Map the local Create-workspace wizard selections onto the server POST payload.
 * Widgets from the static local catalogue are intentionally omitted (they have no
 * numeric server widgetId); only selected Application items are sent.
 */
export const buildCreateWorkspacePayload = ({
  name,
  privacy,
  layout,
  teamId,
  template,
  selectedAppsWidgets
}: BuildCreateWorkspacePayloadArgs): CreateWorkspacePayload => {
  const selectedApps = selectedAppsWidgets?.apps || []

  const items: CreateWorkspaceItem[] = selectedApps.map((app, index) => {
    const item: CreateWorkspaceItem = {
      itemType: 'Application',
      applicationId: resolveApplicationId(app),
      position: index,
      configuration: ''
    }
    // Only send widgetId when a real server widget id is available.
    const widgetId = toNumericId(app?.widgetId)
    if (widgetId) item.widgetId = widgetId
    return item
  })

  const payload: CreateWorkspacePayload = {
    name: name?.trim() ?? '',
    privacy: privacy === 'team' ? WORKSPACE_SCOPE.TEAM : WORKSPACE_SCOPE.PERSONAL,
    layout: layout ? LAYOUT_VARIANT_TO_SERVER[layout.variant] : 'SinglePrimary',
    includeAliPanel: Boolean(layout?.aliPanel),
    items
  }

  // Only include optional ids when explicitly set.
  const resolvedTeamId = resolveTeamId(teamId)
  if (resolvedTeamId !== undefined) payload.teamId = resolvedTeamId

  const resolvedTemplateId = resolveTemplateWorkspaceId(template)
  if (resolvedTemplateId) payload.templateWorkspaceId = resolvedTemplateId

  return payload
}

/** Shape of a single item returned by GET /api/workspaces/{id}. */
export interface WorkspaceDetailItem {
  id?: number
  itemType: 'Application'
  applicationId: number
  widgetId?: number | null
  position: number
  configuration?: string | null
}

/** Shape of the workspace returned by GET /api/workspaces/{id}. */
export interface WorkspaceDetail {
  id: number
  name: string
  privacy: WorkspaceScope
  teamId?: number | string | null
  /** GUID of the owning team as returned by the API (POST/PUT expect it as `teamId`). */
  teamExternalId?: string | null
  layout: string
  includeAliPanel: boolean
  templateWorkspaceId?: number | null
  items?: WorkspaceDetailItem[]
}

/**
 * Build the PUT payload for an existing workspace from the GET /api/workspaces/{id}
 * response, applying any field overrides (e.g. a renamed `name`). Existing layout,
 * privacy and items are preserved so the update does not clobber them. Null optional
 * ids (teamId, templateWorkspaceId, per-item widgetId) are omitted.
 */
export const buildUpdateWorkspacePayload = (
  detail: WorkspaceDetail,
  overrides: Partial<Pick<WorkspaceDetail, 'name'>> = {}
): CreateWorkspacePayload => {
  const items: CreateWorkspaceItem[] = (detail.items || []).map((item, index) => {
    const mapped: CreateWorkspaceItem = {
      itemType: WORKSPACE_ITEM_TYPE.APPLICATION,
      applicationId: toNumericId(item.applicationId),
      position: typeof item.position === 'number' ? item.position : index,
      configuration: item.configuration ?? ''
    }
    const widgetId = toNumericId(item.widgetId)
    if (widgetId) mapped.widgetId = widgetId
    return mapped
  })

  const payload: CreateWorkspacePayload = {
    name: (overrides.name ?? detail.name)?.trim() ?? '',
    privacy:
      detail.privacy === WORKSPACE_SCOPE.TEAM ? WORKSPACE_SCOPE.TEAM : WORKSPACE_SCOPE.PERSONAL,
    layout: detail.layout || 'SinglePrimary',
    includeAliPanel: Boolean(detail.includeAliPanel),
    items
  }

  const resolvedTeamId = resolveTeamId(detail.teamExternalId ?? detail.teamId)
  if (resolvedTeamId !== undefined) payload.teamId = resolvedTeamId

  const resolvedTemplateId = toNumericId(detail.templateWorkspaceId)
  if (resolvedTemplateId) payload.templateWorkspaceId = resolvedTemplateId

  return payload
}
